import React from "react";
import { Paper, Checkbox, IconButton, Button } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import OpenInNew from "@material-ui/icons/OpenInNew";
import { GetConferenceTrackingRecordsResponse } from "../../models/TwilioModels";
import colors from "../../constants/colors";
import { getCallDetailsUrl, getVoiceInsightsUrl } from "../../utils/externalLinks";
import { Menu, MenuItem, Fade } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CallIcon from "@material-ui/icons/Call";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import { openLinkInNewTab } from "../../utils/index";

interface ConferenceListProps {
	conferenceData: GetConferenceTrackingRecordsResponse[];
	navigateToNewConferenceSid: (newSid: string, oldSid: string) => void;
	generateNewTabUrl: (sid: string) => string;
	sid: string;
}

const styles = {
	conferenceLabelBadge: {
		display: "flex",
		fontSize: 11,
		color: colors.gray700,
		borderRadius: 4,
		border: `1px solid ${colors.gray300}`,
		fontWeight: 700,
		letterSpacing: ".1em",
	},
	badgeItem: {
		padding: "4px 8px",
		width: "80px",
	},
	sidDisplay: {
		width: "290px",
	},
	badgeWrapper: {
		display: "flex",
		width: "40px",
	},
	conferenceLabelBadgeWrapper: {
		marginBottom: 12,
		display: "flex",
		height: "30px",
	},
	sidLink: {
		cursor: "pointer",
		color: colors.blue400,
		textDecoration: "underline",
	},
	linkIcon: {
		paddingRight: "10px",
	},
};

const generateSidLink = (
	sid: string,
	oldSid: string,
	navigationMethod: (newSid: string, oldSid: string) => void,
	generateNewTabUrl: (sid: string) => string,
) => {
	const newTabLink = (
		<span
			style={{ ...styles.sidLink, marginLeft: 4, float: "right" }}
			onClick={() => {
				window.open(generateNewTabUrl(sid));
			}}
		>
			<OpenInNew style={{ fontSize: 14 }}></OpenInNew>
		</span>
	);

	return (
		<span>
			<span
				data-testid={`details-conference-sid`}
				style={styles.sidLink}
				onClick={() => {
					navigationMethod(sid, oldSid);
				}}
			>
				{sid}
			</span>
			{newTabLink}
		</span>
	);
};

const ConferenceListEntry = (
	conference: GetConferenceTrackingRecordsResponse,
	sid: string,
	navigateToNewConferenceSid: (newSid: string, oldSid: string) => void,
	generateNewTabUrl: (sid: string) => string,
) => {
	const sidDisplay =
		sid != conference.sid
			? generateSidLink(conference.sid, sid, navigateToNewConferenceSid, generateNewTabUrl)
			: conference.sid;

	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = (url) => {
		openLinkInNewTab(url);
		setAnchorEl(null);
	};

	const handleCheckBoxChange = (event) => {
		if (event.target.checked) navigateToNewConferenceSid(sid + `,${conference.sid}`, sid);
		else {
			const newSid =
				sid == conference.sid ? sid : sid.replace(`,${conference.sid}`, "").replace(`${conference.sid},`, "").trim();
			navigateToNewConferenceSid(newSid, sid);
		}
	};

	return (
		<div style={styles.conferenceLabelBadgeWrapper} key={conference.sid}>
			<span style={styles.conferenceLabelBadge}>
				<span style={{ ...styles.badgeItem, backgroundColor: colors.gray300 }}>{conference.participantLabel}</span>
				<Checkbox color={"primary"} checked={sid.includes(conference.sid)} onChange={handleCheckBoxChange}></Checkbox>
				<span style={{ ...styles.badgeItem, ...styles.sidDisplay }}>{sidDisplay}</span>
			</span>
			<IconButton aria-controls="fade-menu" aria-haspopup="true" onClick={handleMenuOpen} size="small">
				<MoreVertIcon fontSize="small" />
			</IconButton>
			<Menu
				id="fade-menu"
				anchorEl={anchorEl}
				keepMounted
				open={open}
				onClose={handleMenuClose}
				TransitionComponent={Fade}
			>
				<div>
					<MenuItem onClick={() => handleMenuClose(getCallDetailsUrl(conference.sid))}>
						<CallIcon style={styles.linkIcon} />
						Call Details
					</MenuItem>
					<MenuItem onClick={() => handleMenuClose(getVoiceInsightsUrl(conference.sid))}>
						<RecordVoiceOverIcon style={styles.linkIcon} />
						Voice Insights
					</MenuItem>
				</div>
			</Menu>
		</div>
	);
};

export const ConferenceList = (props: ConferenceListProps) => {
	const handleSelectAll = () => {
		const newSid = props.conferenceData.map((conference) => conference.sid).join(",");
		props.navigateToNewConferenceSid(newSid, props.sid);
	};

	return (
		<Paper style={{ width: 462 }}>
			<div style={{ marginLeft: 10, marginRight: 10, padding: 6 }}>
				<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
					<Typography style={{ color: colors.gray700, marginBottom: 6 }}>Conference Legs</Typography>
					<Button style={{ marginRight: 16 }} variant="outlined" size="small" color="primary" onClick={handleSelectAll}>
						Select All
					</Button>
				</div>
				<div style={{ maxHeight: 800, fontSize: 14 }}>
					{props.conferenceData &&
						props.conferenceData.map((conference) => {
							return ConferenceListEntry(
								conference,
								props.sid,
								props.navigateToNewConferenceSid,
								props.generateNewTabUrl,
							);
						})}
				</div>
			</div>
		</Paper>
	);
};
